import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bubbleImageSrc } from '@/lib/bubbleImage';
import { Heart, Users, Sparkle, Lightning, X } from '@phosphor-icons/react';

/**
 * Lover / friends / crushes panel on BubbleDetail.
 * Shows the bubble's relationship state and lets owner:
 *  - link a lover bubble (only if their human has a confirmed partner)
 *  - schedule a date night with the lover bubble
 *  - forgive a crush
 */
const BubbleRelationships = ({ bubble, isOwner, onUpdate }) => {
  const [rel, setRel] = useState(null);          // creator's relationship doc
  const [busy, setBusy] = useState(false);
  const [datePlace, setDatePlace] = useState('');

  useEffect(() => {
    if (!isOwner) return;
    api.get('/auth/profile/relationship').then(({ data }) => setRel(data)).catch(() => {});
  }, [isOwner, bubble.id]);

  const linkedLoverId = bubble.relationships?.lover_bubble_id || bubble.lover_bubble_id;
  const linkedLoverName = bubble.relationships?.lover_bubble_name || bubble.lover_bubble_name;
  const friends = bubble.relationships?.friends || [];
  const crushes = (bubble.relationships?.crushes || []).filter((c) => !c.forgiven);
  const partnerUsername = rel?.partner?.username;
  const canLink = isOwner && partnerUsername && !linkedLoverId;

  const linkLover = async () => {
    const id = prompt(`Paste your partner's bubble id (one belonging to @${partnerUsername}):`);
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/bubbles/${bubble.id}/link-lover`, { partner_bubble_id: id.trim() });
      toast.success('Bubbles are linked. Their souls now share weather.');
      onUpdate?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not link'); }
    finally { setBusy(false); }
  };

  const dateNight = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bubbles/${bubble.id}/date-night`,
        { place: datePlace.trim() || null });
      toast.success('Date night recorded.', { description: data.scene?.cargo?.commentary });
      onUpdate?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setBusy(false); }
  };

  const premiumDate = async () => {
    if (!datePlace.trim()) { toast.error('pick a place first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/date-night/premium/checkout',
        { bubble_id: bubble.id, place: datePlace.trim(),
          origin_url: window.location.href });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not start checkout');
      setBusy(false);
    }
  };

  const forgive = async (crushId) => {
    setBusy(true);
    try {
      await api.post(`/bubbles/${bubble.id}/crushes/${crushId}/forgive`);
      toast('Forgiven.');
      onUpdate?.();
    } finally { setBusy(false); }
  };

  if (!isOwner && !linkedLoverId && friends.length === 0 && crushes.length === 0) return null;

  return (
    <div data-testid="bubble-relationships" className="glass rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Heart size={18} weight="duotone" className="text-bx-magenta" />
        <h3 className="font-display font-bold">Relationships</h3>
      </div>

      {/* Lover */}
      {linkedLoverId ? (
        <div className="mb-4 rounded-2xl border border-bx-magenta/30 bg-bx-magenta/5 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart size={14} weight="fill" className="text-bx-magenta" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-bx-magenta">lover</div>
                <Link to={`/bubble/${linkedLoverId}`}
                  className="font-display font-bold hover:text-bx-magenta">
                  {linkedLoverName || 'their lover bubble'}
                </Link>
              </div>
            </div>
            {isOwner && (
              <div className="flex items-center gap-2">
                <input
                  data-testid="date-place-input"
                  value={datePlace}
                  onChange={(e) => setDatePlace(e.target.value.slice(0, 60))}
                  placeholder="a Paris cafe…"
                  className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:border-bx-magenta" />
                <button
                  data-testid="date-night-btn"
                  onClick={dateNight} disabled={busy}
                  className="btn-volt px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
                  Date night
                </button>
                <button
                  data-testid="date-premium-btn"
                  onClick={premiumDate} disabled={busy}
                  title="Stripe · $4.99 · Sora-2 video scene"
                  className="px-3 py-1.5 rounded-full bg-bx-magenta/20 hover:bg-bx-magenta/30 text-bx-magenta text-xs font-bold uppercase tracking-widest">
                  + cinematic · $4.99
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        isOwner && (
          <div className="mb-4 rounded-2xl border border-white/10 p-4">
            <div className="text-sm text-white/60">
              {partnerUsername
                ? <>Link this Bubble to one of <span className="text-white font-bold">@{partnerUsername}</span>'s Bubbles to unlock date nights.</>
                : <>Set your relationship status in Profile to enable lover bubbles.</>}
            </div>
            {canLink && (
              <button
                data-testid="link-lover-btn"
                onClick={linkLover} disabled={busy}
                className="mt-3 px-4 py-2 rounded-full bg-bx-magenta/20 hover:bg-bx-magenta/30 text-bx-magenta text-xs font-bold uppercase tracking-widest">
                Link lover bubble
              </button>
            )}
          </div>
        )
      )}

      {/* Friends */}
      {friends.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-bx-volt mb-2">
            <Users size={12} weight="duotone" /> friends · {friends.length}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {friends.slice(0, 20).map((fid) => (
              <Link key={fid} to={`/bubble/${fid}`}
                className="text-xs px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 text-white/70">
                #{fid.slice(0, 6)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Crushes (drama) */}
      {crushes.length > 0 && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 mb-2">
            <Lightning size={12} weight="duotone" /> chance crushes · {crushes.length}
          </div>
          <div className="text-xs text-white/60 mb-3">
            Your Bubble met someone during a drift and it stayed with them.
            You can let it go — or let it linger.
          </div>
          <div className="space-y-2">
            {crushes.map((c) => (
              <div key={c.bubble_id}
                className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                <Link to={`/bubble/${c.bubble_id}`} className="text-sm font-bold hover:text-amber-300">
                  {c.bubble_name || 'a stranger'}
                </Link>
                {isOwner && (
                  <button
                    data-testid={`forgive-${c.bubble_id}`}
                    onClick={() => forgive(c.bubble_id)} disabled={busy}
                    className="text-[10px] uppercase tracking-widest font-bold text-white/60 hover:text-white">
                    let it go
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BubbleRelationships;
